import axios, { AxiosRequestConfig } from 'axios';

import { ReviewCategoryLang } from 'types/translation';
import { getSessionId } from 'utils/sessionStorage';
import { API } from './constants';
import { withRetry } from './retry';

const apiInstance = axios.create({
  baseURL: window.APP_BASE_URL,
});

const getRequestConfig = (): AxiosRequestConfig => ({
  headers: {
    authorization: getSessionId(),
  },
});

function setIdAndVersion(url: string, id: string, version: string): string {
  return url
    .replace('document_id', id)
    .replace('document_version', version.replace('.', '/'));
}

export function getMetaForDocumentId(id: string, version: string) {
  return apiInstance.get(
    document.location.host.indexOf('localhost') === -1
      ? setIdAndVersion(API.DOCUMENTS, id, version)
      : 'http://localhost:5001/api/versions',
    getRequestConfig()
  );
}

export function getAssessment(
  id: string,
  version: string,
  ssiDocumentNumbers: string[] = []
) {
  let uri = setIdAndVersion(API.ASSESSMENT, id, version);
  const isSsiDocumentNumbers = ssiDocumentNumbers.length > 0;

  if (isSsiDocumentNumbers) {
    const listSsiDocuments = ssiDocumentNumbers
      .join('&ssiDocumentNumbers=')
      .split(',');
    uri = uri.replace('[ssiDocumentNumbers]', `${listSsiDocuments}`);
  } else {
    uri = uri.replace('?ssiDocumentNumbers=[ssiDocumentNumbers]', '');
  }

  return apiInstance.get(
    document.location.host.indexOf('localhost') === -1
      ? uri
      : 'http://localhost:5001/api/assessment',
    getRequestConfig()
  );
}

export let abortPageController = new AbortController();

export function getAssessmentPage(pageNumber: number, token: string) {
  const uri = API.ASSESSMENT_PAGE.replace(
    '[page_number]',
    `${pageNumber}`
  ).replace('[token]', token);

  abortPageController = new AbortController();

  return withRetry(
    apiInstance.get,
    document.location.host.indexOf('localhost') === -1
      ? uri
      : 'http://localhost:5001/api/page',
    {
      ...getRequestConfig(),
      signal: abortPageController.signal,
    }
  );
}

export function getAssessmentSummary(token: string) {
  const uri = API.ASSESSMENT_SUMMARY.replace('[token]', token);
  return withRetry(
    apiInstance.get,
    document.location.host.indexOf('localhost') === -1
      ? uri
      : 'http://localhost:5001/api/summary',
    getRequestConfig()
  );
}

export function getExternalSession(code: string) {
  return apiInstance.post(API.AUTHORIZE_VEEVA_SESSION, { code });
}

export type IAssessmentReportResponse = {
  reportUrl: string;
  documentName: string;
};

export function getAssessmentReport(
  token: string,
  reportType: ReviewCategoryLang
) {
  return apiInstance.get<IAssessmentReportResponse>(
    API.ASSESSMENT_REPORT.replace('[token]', token).replace(
      '[reportType]',
      reportType
    ),
    getRequestConfig()
  );
}
